import React, { useRef, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import html2pdf from 'html2pdf.js';

const InvoicePreviewDialog = ({ open, onClose, html }) => {
  const iframeRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState('800px');

  useEffect(() => {
    if (open && iframeRef.current) {
      const iframe = iframeRef.current;
      
      const resizeIframe = () => {
        try {
          if (iframe.contentWindow && iframe.contentWindow.document.body) {
            const contentHeight = iframe.contentWindow.document.body.scrollHeight;
            setIframeHeight(`${contentHeight}px`);
          }
        } catch (error) {
          // Cross-origin or loading error, set default height
          setIframeHeight('800px');
        }
      };

      iframe.addEventListener('load', resizeIframe);
      
      // Also try to resize after a short delay
      const timer = setTimeout(resizeIframe, 100);

      return () => {
        iframe.removeEventListener('load', resizeIframe);
        clearTimeout(timer);
      };
    }
  }, [open, html]);

  const handlePrint = () => {
    if (iframeRef.current) {
      const iframe = iframeRef.current;
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  const handleDownload = async () => {
    try {
      const opt = {
        margin: [20, 15, 20, 15],
        filename: `delivery-note-${Date.now()}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2, 
          useCORS: true, 
          logging: false,
          width: 793.7,
          windowWidth: 793.7,
          letterRendering: true
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        },
        pagebreak: { 
          mode: ['avoid-all', 'css', 'legacy'],
          before: '.page-break-before',
          after: '.page-break-after',
          avoid: ['tr', 'td', 'img', '.totals-section', '.notes-section']
        }
      };

      await html2pdf().set(opt).from(html).save();
    } catch (error) {
      console.error('PDF download error:', error);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '900px',
          maxWidth: '95vw',
          height: '95vh',
          maxHeight: '95vh',
          m: 2
        }
      }}
    >
      <Box sx={{ 
        position: 'relative', 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <IconButton 
          onClick={onClose}
          sx={{ 
            position: 'absolute', 
            right: 16, 
            top: 16, 
            zIndex: 1300,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            '&:hover': { 
              bgcolor: 'white',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto', 
          bgcolor: '#525659',
          display: 'flex',
          justifyContent: 'center',
          p: 3
        }}>
          <Box sx={{ 
            width: '210mm',
            bgcolor: 'white',
            boxShadow: '0 0 10px rgba(0,0,0,0.3)'
          }}>
            <iframe
              ref={iframeRef}
              srcDoc={html}
              title="Delivery Note Preview"
              style={{ 
                width: '210mm',
                height: iframeHeight,
                border: 'none', 
                display: 'block',
                overflow: 'hidden'
              }}
            />
          </Box>
        </Box>
        
        <DialogActions sx={{ 
          p: 2, 
          gap: 1, 
          justifyContent: 'flex-end', 
          borderTop: '1px solid #e0e0e0',
          bgcolor: 'white'
        }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ textTransform: 'none' }}
          >
            Print
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ textTransform: 'none' }}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default InvoicePreviewDialog;